
import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSubscriptionManagement } from "@/hooks/account/useSubscriptionManagement";
import { CalendarDays, AlertCircle, CreditCard, ArrowUpCircle, PlusCircle } from "lucide-react";
import { toast } from "sonner";

interface ManageSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string | null;
  plan: "monthly" | "yearly" | null;
  nextBillingDate: string | null;
  isCancelled: boolean;
}

const ManageSubscriptionDialog = ({
  isOpen,
  onClose,
  subscriptionId,
  plan,
  nextBillingDate,
  isCancelled
}: ManageSubscriptionDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { 
    handleCancelSubscription, 
    handleReactivateSubscription,
    handleUpgradeSubscription 
  } = useSubscriptionManagement();

  const formattedBillingDate = nextBillingDate ? 
    new Date(nextBillingDate).toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    }) : null;

  const handleCancel = async () => {
    if (!subscriptionId) {
      toast.error("No subscription ID found");
      return;
    }

    try {
      setIsProcessing(true);
      await handleCancelSubscription(subscriptionId);
      toast.success("Subscription successfully cancelled");
      onClose();
      // Reload the page to refresh subscription status
      window.location.reload();
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast.error("Failed to cancel subscription");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivate = async () => {
    if (!subscriptionId) {
      toast.error("No subscription ID found");
      return;
    }

    try {
      setIsProcessing(true);
      await handleReactivateSubscription(subscriptionId);
      toast.success("Subscription successfully reactivated");
      onClose();
      // Reload the page to refresh subscription status
      window.location.reload();
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error("Failed to reactivate subscription");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgrade = async () => {
    if (!subscriptionId || plan !== "monthly") {
      toast.error("Cannot upgrade subscription");
      return;
    }

    try {
      setIsProcessing(true);
      await handleUpgradeSubscription(subscriptionId);
      toast.success("Subscription successfully upgraded to yearly plan");
      onClose();
      // Reload the page to refresh subscription status
      window.location.reload();
    } catch (error) {
      console.error("Error upgrading subscription:", error);
      toast.error("Failed to upgrade subscription");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Manage Subscription</DialogTitle>
          <DialogDescription>
            Manage your {plan === "monthly" ? "Monthly" : "Yearly"} Premium subscription
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {formattedBillingDate && (
            <div className="flex items-center gap-2 mb-6 p-3 bg-blue-50 rounded-lg text-[#1B2F47]">
              <CalendarDays className="w-5 h-5" />
              <div>
                <p className="font-medium">
                  {isCancelled ? "Your subscription will expire on" : "Your subscription will renew on"}
                </p>
                <p>{formattedBillingDate}</p>
              </div>
            </div>
          )}

          {isCancelled ? (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Reactivate Subscription</h3>
                <p className="text-sm text-gray-600">
                  Your subscription is currently cancelled. Reactivate to continue receiving premium benefits beyond your current billing period.
                </p>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Reactivating will resume billing at the end of your current period.
                </p>
              </div>
            </div>
          ) : plan === "monthly" ? (
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Upgrade to Yearly Plan</h3>
                  <p className="text-sm text-gray-600">
                    Save 2 months by upgrading to our yearly plan. Pay once and get access for a full year.
                  </p>
                </div>

                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                  <PlusCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Yearly plan: $290/year</p>
                    <p>Monthly plan: $29/month ($348/year)</p>
                    <p className="font-medium mt-1">You save: $58 (2 months free!)</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div>
                  <h3 className="font-semibold mb-2">Cancel Subscription</h3>
                  <p className="text-sm text-gray-600">
                    If you cancel, your subscription will remain active until the end of your current billing period. 
                    You will not be charged again after that date.
                  </p>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Cancelling will remove your ability to follow premium strategies after your current billing period ends.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Cancel Subscription</h3>
                <p className="text-sm text-gray-600">
                  If you cancel, your subscription will remain active until the end of your current billing period. 
                  You will not be charged again after that date.
                </p>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Cancelling will remove your ability to follow premium strategies after your current billing period ends.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
          
          {isCancelled ? (
            <Button
              variant="default"
              onClick={handleReactivate}
              disabled={isProcessing}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              {isProcessing ? "Processing..." : "Reactivate Subscription"}
            </Button>
          ) : plan === "monthly" ? (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                variant="default"
                onClick={handleUpgrade}
                disabled={isProcessing}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
              >
                <ArrowUpCircle className="mr-2 h-4 w-4" />
                {isProcessing ? "Processing..." : "Upgrade to Yearly"}
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={isProcessing}
                className="w-full sm:w-auto"
              >
                {isProcessing ? "Cancelling..." : "Cancel Subscription"}
              </Button>
            </div>
          ) : (
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={isProcessing}
              className="w-full sm:w-auto"
            >
              {isProcessing ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageSubscriptionDialog;
